import { doc, updateDoc } from "firebase/firestore";
import JSConfetti from "js-confetti";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useKrpano from "react-krpano-hooks";
import { db } from "../firebase/config";

import { CongratsMessage } from "../components/CongratsMessage";
import { InitialBanner } from "../components/InitialBanner";
import { TeamComponent } from "../components/Team";
import { TeamSelect } from "../components/TeamSelect";
import useZoneEditor, {
  useAddDefaultHotSpots,
  useAddHotSpots,
} from "../hooks/useZoneEditor";
import { WinnerBanner } from "../components/WinnerBanner";
import { AwardReveal } from "../components/AwardReveal";
import { useMatch } from "../hooks/useMatch";
import { useAds } from "../hooks/useAds";
import { Award, Match } from "../types";

import "./MatchView.css";

const jsConfetti = new JSConfetti();

const MatchView = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const { data, loading, notFound } = useMatch(matchId);

  useEffect(() => {
    if (data) {
      document.title = `${data.local.teamName} vs ${data.visitante.teamName} - Gigamatch`;
    } else {
      document.title = "Gigamatch";
    }
  }, [data]);

  const [initialBanner, setInitialBanner] = useState(true);
  const [showBannerTeamSelect, setShowBannerTeamSelect] = useState(false);
  const [showCongratsMessage, setShowCongratsMessage] = useState(false);
  const [showWinnerBanner, setShowWinnerBanner] = useState(false);
  const [isLocalWinner, setIsLocalWinner] = useState(false);
  const [isLocal, setIsLocal] = useState(false);
  const [currentSpot, setCurrentSpot] = useState("");
  const [revealedAward, setRevealedAward] = useState<Award | null>(null);

  useEffect(() => {
    if (showCongratsMessage) {
      setTimeout(() => {
        setShowCongratsMessage(false);
      }, 3000);
    }
  }, [showCongratsMessage]);

  const showWinner = useCallback(
    (data: Match) => {
      const finalMatch =
        data?.totalSeats <=
        data?.local.assignedSeats + data?.visitante.assignedSeats;
      if (finalMatch) {
        setInitialBanner(false);
        setShowCongratsMessage(false);
        setShowWinnerBanner(true);

        const localPercentage =
          (data.local.assignedSeats * 100) / data.totalSeats;
        const visitPercentage =
          (data.visitante.assignedSeats * 100) / data.totalSeats;

        setIsLocalWinner(localPercentage > visitPercentage);

        const winner = data[isLocalWinner ? "local" : "visitante"];

        jsConfetti.addConfetti({
          confettiColors: [winner.teamColor, winner.teamColorSecondary],
          confettiRadius: 6,
          confettiNumber: 500,
        });
      }
    },
    [isLocalWinner]
  );

  useEffect(() => {
    showWinner(data as Match);
  }, [data, showWinner]);

  const { pointClicked, zones } = useZoneEditor();

  const { containerRef, callKrpano, setKrpano } = useKrpano({
    scriptPath: "/krpano/krpano.js",
    embeddingParams: { consolelog: true, xml: '/krpano/tour.xml', },
    globalFunctions: {
      hotSpotClicked: (hsName) => {
        setCurrentSpot(hsName);
        !showCongratsMessage && setShowBannerTeamSelect(true);
      },
      pointClicked,
    },
  });


  useAddHotSpots(zones, callKrpano, setKrpano);
  useAddDefaultHotSpots(callKrpano, setKrpano, data?.sillas || {}, {
    visitante: data?.visitante.teamColor || "",
    local: data?.local.teamColor || "",
  });
  useAds(callKrpano, setKrpano, data?.ads || []);

  const updateSeatForTeam = async (localTeam: boolean) => {
    if (!data || !matchId) {
      return;
    }

    setIsLocal(localTeam);

    const matchDocument = doc(db, "match", matchId);

    const { visitante, local, sillas } = data;

    if (localTeam) {
      const newSeats = local.assignedSeats + 1;
      await updateDoc(matchDocument, {
        local: { ...local, assignedSeats: newSeats },
        sillas: { ...sillas, [currentSpot]: "local" },
      });
    } else {
      const newSeats = visitante.assignedSeats + 1;
      await updateDoc(matchDocument, {
        visitante: { ...visitante, assignedSeats: newSeats },
        sillas: { ...sillas, [currentSpot]: "visitante" },
      });
    }

    setShowBannerTeamSelect(false);
    setShowCongratsMessage(true);
    jsConfetti.addConfetti({
      confettiRadius: 6,
      confettiNumber: 500,
    });

    // Check for hidden award
    const awardId = data.hiddenAwards?.[currentSpot];
    if (awardId && !data.claimedAwards?.[currentSpot]) {
      const award = data.awards?.find((a) => a.id === awardId);
      if (award) {
        // Mark as claimed in Firestore
        await updateDoc(matchDocument, {
          [`claimedAwards.${currentSpot}`]: true,
        });
        setTimeout(() => {
          setRevealedAward(award);
          jsConfetti.addConfetti({
            confettiColors: ["#ffd700", "#ffec8b", "#daa520", "#ffffff"],
            confettiRadius: 8,
            confettiNumber: 800,
          });
        }, 1500);
      }
    }
  };

  const closeInitialBanner = () => {
    setInitialBanner(false);
  };

  return (
    <>
      {loading && (
        <div className="match-status">
          <p>Cargando partido...</p>
        </div>
      )}

      {notFound && (
        <div className="match-status">
          <h2>Partido no encontrado</h2>
          <p>El enlace del partido no es válido o ha sido eliminado.</p>
        </div>
      )}

      {data && (
        <>
          <div
            style={{
              position: "absolute",
              zIndex: 10,
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              pointerEvents: "none",
            }}
          >
            <TeamComponent
              team={data?.local}
              active={
                !initialBanner && !showBannerTeamSelect && !showWinnerBanner
              }
            />
            <TeamComponent
              team={data?.visitante}
              active={
                !initialBanner && !showBannerTeamSelect && !showWinnerBanner
              }
            />
          </div>
          <InitialBanner
            data={data}
            active={initialBanner}
            closeInitialBanner={closeInitialBanner}
          />
          <TeamSelect
            data={data}
            active={showBannerTeamSelect}
            updateSeatForTeam={updateSeatForTeam}
          />
          <CongratsMessage
            name={data?.[isLocal ? "local" : "visitante"].teamName}
            active={showCongratsMessage}
          />
          <WinnerBanner
            data={data[isLocalWinner ? "local" : "visitante"]}
            active={showWinnerBanner}
          />
          {revealedAward && (
            <AwardReveal
              award={revealedAward}
              active={!!revealedAward}
              onClose={() => setRevealedAward(null)}
            />
          )}
        </>
      )}

      <div ref={containerRef as any} />
    </>
  );
};

export default MatchView;
